import * as React from 'react';
import { Form, Formik } from 'formik';
import { SchoolUserPermission } from '../../../../../../models/schoolUser';
import propz from 'propz';
import {
  ALL_SCHOOL_ROLES_ADDING_LIST,
  MAX_USER_PRIORITY,
  MIN_USER_PRIORITY,
  ROLE,
  ROLE_SERVER_TO_CLIENT_MAPPING
} from '../../../../../../consts/user';
import { Autocomplete } from '../../../../../../components/Autocomplete/Autocomplete';
import {
  getFullName,
  getName,
  searchFunctionForms,
  searchFunctionHouses,
  searchFunctionSchoolStudents,
  searchFunctionSportsNinSportIds
} from '../../../../../../helpers/autocomplete/autocomplete';
import { AppUser } from '../../../../../App/App';
import * as Yup from 'yup';
import './UserAddRoleForm.css';

interface Props {
  user: AppUser;
  userPermissions: SchoolUserPermission[];
  onCloseClick: () => void;
  onSubmit: (values) => void;
  initialPreset?: string;
  affectedUsersCount: number;
}

interface State {
  form: any;
  house: any;
  isUserAlreadyLinkedError: boolean;
  sportPickerKey: number;
}

export class UserAddRoleForm extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      form: undefined,
      house: undefined,
      isUserAlreadyLinkedError: false,
      sportPickerKey: 0
    };
  }

  isUserAlreadyLinkedToStudent = (studentId: string): boolean => {
    const { userPermissions } = this.props;
    return userPermissions.some(permission => permission.preset === ROLE.PARENT && String(permission.studentId) === String(studentId));
  };

  handleSubmit = (values): void => {
    const { studentId } = values;

    if (values.preset === ROLE.PARENT && this.isUserAlreadyLinkedToStudent(studentId)) {
      this.setState({ isUserAlreadyLinkedError: true });
    } else {
      this.props.onSubmit(values);
    }
  };

  getAvailableRoles = (): string[] => {
    const { userPermissions, user } = this.props;
    const existedRoles = userPermissions.map(role => role.preset);
    const rolesAllowedForCaller =
      user.activeRole === ROLE.MANAGER
        ? ALL_SCHOOL_ROLES_ADDING_LIST.filter(role => role !== ROLE.ADMIN && role !== ROLE.MANAGER)
        : ALL_SCHOOL_ROLES_ADDING_LIST;
    return [...rolesAllowedForCaller].filter(
      role => role === ROLE.PARENT || existedRoles.every(existedRole => existedRole !== role)
    );
  };

  getForms = text => {
    if (text === '') {
      this.setState({ form: undefined });
    }
    const { user } = this.props;
    return searchFunctionForms(user, text);
  };

  getHouses = text => {
    if (text === '') {
      this.setState({ house: undefined });
    }
    const { user } = this.props;
    return searchFunctionHouses(user, text);
  };

  getSports = (text: string, sportIds) => {
    const { user } = this.props;
    return searchFunctionSportsNinSportIds(user, text, sportIds);
  };

  getSchoolStudents = (text, setFieldValue) => {
    const { form, house } = this.state;
    if (text === '') {
      setFieldValue('studentId', '');
      setFieldValue('student', undefined);
      this.setState({ house: undefined, form: undefined });
    }
    const user = this.props.user;
    return searchFunctionSchoolStudents(user, form, house, text).then(students =>
      students.filter(student => !this.isUserAlreadyLinkedToStudent(student.id))
    );
  };

  renderPriorityOptions = (): React.ReactNode => {
    let priorityOptions = [];

    for (let index = MIN_USER_PRIORITY; index <= MAX_USER_PRIORITY; index++) {
      priorityOptions.push(
        <option key={index} value={index}>
          {index}
        </option>
      );
    }
    return priorityOptions;
  };

  renderFieldShell(label: string, children: React.ReactNode, hint?: string, error?: React.ReactNode): React.ReactNode {
    return (
      <label className="userRoleForm-field">
        <span className="userRoleForm-label">{label}</span>
        {hint ? <span className="userRoleForm-hint">{hint}</span> : null}
        {children}
        {error ? <span className="userRoleForm-error">{error}</span> : null}
      </label>
    );
  }

  renderParentRoleChip(): React.ReactNode {
    return (
      <div className="userRoleForm-roleStrip">
        <span className="userRoleForm-roleChip">
          <span className="userRoleForm-roleChipDot" />
          {ROLE_SERVER_TO_CLIENT_MAPPING[ROLE.PARENT]}
        </span>
      </div>
    );
  }

  renderStudentPreview(student: any): React.ReactNode {
    if (!student) {
      return null;
    }

    const formName = propz.get(student, ['form', 'name'], 'No form');
    const houseName = propz.get(student, ['house', 'name'], 'No house');

    return (
      <div className="userRoleForm-preview">
        <span className="userRoleForm-avatar">
          <span>
            {String(propz.get(student, ['firstName'], '?')).charAt(0)}
            {String(propz.get(student, ['lastName'], '')).charAt(0)}
          </span>
        </span>
        <div>
          <strong>{getFullName(student)}</strong>
          <span>
            {formName} · {houseName}
          </span>
        </div>
      </div>
    );
  }

  renderStudentOption(student: any): React.ReactNode {
    const formName = propz.get(student, ['form', 'name'], 'No reg form');
    const houseName = propz.get(student, ['house', 'name'], 'No house');
    const birthday = propz.get(student, ['birthday'], '');
    const birthdayText = birthday ? new Date(birthday).toLocaleDateString('en-GB') : 'Not provided';

    return (
      <div className="userRoleForm-studentOption">
        <strong>{getFullName(student)}</strong>
        <span>
          Reg form: {formName} · House: {houseName} · DOB: {birthdayText}
        </span>
      </div>
    );
  }

  render() {
    const initialPreset = this.props.initialPreset || '';
    const roleInitial = {
      preset: initialPreset,
      sports: [],
      sportIds: [],
      priority: initialPreset === ROLE.PARENT ? String(MIN_USER_PRIORITY) : '0',
      student: undefined,
      studentId: ''
    };

    const RoleSchema = Yup.object().shape({
      preset: Yup.string().required('Required'),
      sportIds: Yup.array(),
      studentId: Yup.string().test('studentId', 'Required', function(value) {
        const isNoParentRole = this.parent.preset !== ROLE.PARENT;
        const isExistStudentId = typeof value !== 'undefined' && value !== '';
        return isNoParentRole || (isExistStudentId && !isNoParentRole);
      })
    });

    const { isUserAlreadyLinkedError } = this.state;

    return (
      <Formik
        onSubmit={this.handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        initialValues={roleInitial}
        validationSchema={RoleSchema}
      >
        {({ touched, errors, values, setFieldValue }) => {
          const isTeacherOrCoachRole = values.preset === ROLE.TEACHER || values.preset === ROLE.COACH;
          const isParentRole = values.preset === ROLE.PARENT;
          const isParentPresetLocked = initialPreset === ROLE.PARENT;
          const { form, house } = this.state;
          const selectRole = (currentRole: string) => {
            setFieldValue('preset', currentRole);
            this.setState({ isUserAlreadyLinkedError: false });

            if (currentRole !== ROLE.TEACHER && currentRole !== ROLE.COACH) {
              setFieldValue('sports', []);
              setFieldValue('sportIds', []);
            }
            if (currentRole !== ROLE.PARENT) {
              setFieldValue('studentId', '');
              setFieldValue('student', undefined);
            }
          };

          return (
            <Form className="userRoleForm">
              <p className="userRoleForm-intro">
                Choose a role and configure the access it gives to the selected user.
              </p>
              <div className="userRoleForm-summary">
                Adding a role to {this.props.affectedUsersCount}{' '}
                {this.props.affectedUsersCount === 1 ? 'user' : 'users'}.
              </div>
              {isParentPresetLocked ? (
                this.renderParentRoleChip()
              ) : (
                <section className="userRoleForm-section userRoleForm-section--first">
                  <div className="userRoleForm-sectionHead">
                    <span>Select role</span>
                  </div>
                  <div className="userRoleForm-roleChoices" role="radiogroup" aria-label="Select role">
                    {this.getAvailableRoles().map(role => (
                      <button
                        key={role}
                        type="button"
                        role="radio"
                        aria-checked={values.preset === role}
                        className="userRoleForm-roleChoice"
                        data-selected={values.preset === role}
                        onClick={() => selectRole(role)}
                      >
                        <span className="userRoleForm-roleChoiceDot" />
                        {ROLE_SERVER_TO_CLIENT_MAPPING[role]}
                      </button>
                    ))}
                  </div>
                  {touched.preset && typeof errors.preset === 'string' ? (
                    <span className="userRoleForm-error">{errors.preset}</span>
                  ) : null}
                </section>
              )}

              {isParentRole && (
                <section className="userRoleForm-section userRoleForm-section--parentCompact">
                  <div className="userRoleForm-grid userRoleForm-grid--three">
                    {this.renderFieldShell(
                      'Form',
                      <Autocomplete
                        searchFunction={this.getForms}
                        getElementTitle={getName}
                        customClass="userRoleForm-autocomplete"
                        isMenuPortal
                        menuZIndex={2147483200}
                        defaultItem={form}
                        onSelect={form => {
                          this.setState({ form });
                        }}
                      />
                    )}
                    {this.renderFieldShell(
                      'House',
                      <Autocomplete
                        searchFunction={this.getHouses}
                        getElementTitle={getName}
                        customClass="userRoleForm-autocomplete"
                        isMenuPortal
                        menuZIndex={2147483200}
                        defaultItem={house}
                        onSelect={house => {
                          this.setState({ house });
                        }}
                      />
                    )}
                    {this.renderFieldShell(
                      'Priority',
                      <select
                        name="priority"
                        className="userRoleForm-select"
                        value={values.priority}
                        onChange={event => setFieldValue('priority', event.target.value)}
                      >
                        {this.renderPriorityOptions()}
                      </select>
                    )}
                  </div>
                  {this.renderFieldShell(
                    'Child',
                    <Autocomplete
                      searchFunction={text => this.getSchoolStudents(text, setFieldValue)}
                      getElementTitle={getFullName}
                      renderItem={student => this.renderStudentOption(student)}
                      customClass="userRoleForm-autocomplete"
                      isMenuPortal
                      menuZIndex={2147483200}
                      defaultItem={values.student}
                      onSelect={student => {
                        this.setState({ isUserAlreadyLinkedError: false });
                        setFieldValue('studentId', student.id);
                        setFieldValue('student', student);
                        const form = propz.get(student, ['form']);
                        const house = propz.get(student, ['house']);
                        this.setState({ form, house });
                      }}
                    />,
                    undefined,
                    touched.studentId && typeof errors.studentId === 'string' ? errors.studentId : null
                  )}
                  {this.renderStudentPreview(values.student)}
                  {isUserAlreadyLinkedError && (
                    <div className="userRoleForm-alert">This parent is already linked to the selected child.</div>
                  )}
                </section>
              )}

              {isTeacherOrCoachRole && (
                <section className="userRoleForm-section">
                  <div className="userRoleForm-sectionHead">
                    <span>Sports</span>
                  </div>
                  <div className="userRoleForm-chipList">
                    {values.sports.length === 0 ? (
                      <span className="userRoleForm-empty userRoleForm-empty--sports">
                        If no sports are selected, this user will have access to all sports.
                      </span>
                    ) : null}
                    {values.sports.map((sport, index) => (
                      <span key={`sport_tag_${sport.id}`} className="userRoleForm-chip">
                        <i />
                        <span>{sport.name}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const ids = [...values.sportIds];
                            const sports = [...values.sports];
                            ids.splice(index, 1);
                            sports.splice(index, 1);
                            setFieldValue('sportIds', ids);
                            setFieldValue('sports', sports);
                          }}
                          aria-label={`Remove ${sport.name}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  {this.renderFieldShell(
                    'Add sport',
                    <Autocomplete
                      key={`sport_picker_${this.state.sportPickerKey}`}
                      searchFunction={text => this.getSports(text, values.sportIds)}
                      getElementTitle={getName}
                      customClass="userRoleForm-autocomplete"
                      isMenuPortal
                      menuZIndex={2147483200}
                      onSelect={sport => {
                        const sportIds = [...values.sportIds];
                        const sports = [...values.sports];
                        if (sportIds.findIndex(id => id === sport.id) === -1) {
                          sportIds.push(sport.id);
                          sports.push(sport);
                          setFieldValue('sportIds', sportIds);
                          setFieldValue('sports', sports);
                          this.setState(state => ({ sportPickerKey: state.sportPickerKey + 1 }));
                        }
                      }}
                    />,
                    undefined,
                    touched.sportIds && typeof errors.sportIds === 'string' ? errors.sportIds : null
                  )}
                </section>
              )}

              <div className="userRoleForm-actions">
                <button type="button" className="sit-btn sit-btn--ghost" onClick={this.props.onCloseClick}>
                  Cancel
                </button>
                <button type="submit" className="sit-btn sit-btn--primary">
                  {isParentRole ? 'Add child' : 'Add role'}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    );
  }
}
